import React from "react";
import { Box, Typography } from "@mui/joy";
import { Download, RotateCw, FileDown } from "lucide-react";
import { DocumentPreviewWidget } from "../../";
import { useDocument, useAsset } from "../../../hooks/query";
import { useNotification } from "../../../hooks/useNotification";
import { useProject } from "../../../hooks";
import { useContractSigning } from "./hooks";

export function ContractReview() {
    const { project } = useProject();
    const { contractDocument, claimsEstimateDocument, refreshContract } = useContractSigning();

    const { useGenerateContract, useClaimsEstimateCreate } = useDocument();
    const { useDownload } = useAsset();
    const { showSuccess, showError } = useNotification();

    const generateContractSvc = useGenerateContract();
    const generateClaimsEstimateSvc = useClaimsEstimateCreate();
    const downloadAssetSvc = useDownload();

    const handleRegenerateContract = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateContractSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Contract regenerated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error regenerating contract:", error);
            showError("Failed to regenerate contract");
        }
    };

    const handleDownloadClaimsEstimate = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            const data = await generateClaimsEstimateSvc.mutateAsync({ ProjectId: project.id });

            if (data?.assetId) {
                await downloadAssetSvc.mutateAsync({ AssetId: data.assetId });
                showSuccess("Claims Estimate downloaded successfully");
            } else {
                showError("Failed to generate download link");
            }

            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error downloading claims estimate:", error);
            showError("Failed to download claims estimate");
        }
    };

    return (
        <Box mt={4} display="flex" flexDirection="column" gap={4}>
            {/* Contract Section */}
            <Box>
                <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                    Documents Review
                </Typography>
                <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                    Formal agreement to define project deliverables, timelines, and obligations.
                </Typography>
                <Box mt={2} display={"flex"} gap={2}>
                    <DocumentPreviewWidget
                        thumbnailUrl={contractDocument?.thumbnailUrl}
                        title="Contract"
                        description="Project agreement and terms"
                        status={contractDocument?.Status || "NOT GENERATED"}
                        assetId={contractDocument?.DocumentAssetId}
                        buttonText={contractDocument ? "Download" : "Generate Contract"}
                        buttonIcon={<FileDown size={16} />}
                        onButtonClick={!contractDocument ? handleRegenerateContract : undefined}
                        loading={generateContractSvc.isPending}
                        menuActions={
                            contractDocument
                                ? [
                                      {
                                          label: "Regenerate",
                                          icon: <RotateCw size={16} />,
                                          onClick: handleRegenerateContract,
                                          disabled: generateContractSvc.isPending,
                                      },
                                  ]
                                : []
                        }
                    />
                    <DocumentPreviewWidget
                        thumbnailUrl={claimsEstimateDocument?.thumbnailUrl}
                        title="Claims Estimate"
                        description="Itemized report for insurance claims"
                        status={claimsEstimateDocument?.Status || "NOT GENERATED"}
                        assetId={claimsEstimateDocument?.DocumentAssetId}
                        buttonText={claimsEstimateDocument ? "Download" : "Generate Estimate"}
                        buttonIcon={<FileDown size={16} />}
                        onButtonClick={!claimsEstimateDocument ? handleDownloadClaimsEstimate : undefined}
                        loading={generateClaimsEstimateSvc.isPending}
                        menuActions={
                            claimsEstimateDocument
                                ? [
                                      {
                                          label: "Regenerate",
                                          icon: <RotateCw size={16} />,
                                          onClick: handleDownloadClaimsEstimate,
                                          disabled: generateClaimsEstimateSvc.isPending,
                                      },
                                  ]
                                : []
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
}
